import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faSpinner,
  faCheckCircle,
  faTimes,
  faSearch,
  faChartBar,
  faInfoCircle,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import adminTicketsService from '../../../helpers/adminTicketsService';

const TicketsDiscrepanciesAnalysis = ({ onClose, clienteId = null }) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState(null);

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    setReport(null);

    try {
      const result = await adminTicketsService.analyzePointsDiscrepancies(clienteId);
      setReport(result);
    } catch (err) {
      console.error('Error analyzing discrepancies:', err);
      setError(err.message || 'Error al analizar discrepancias');
    } finally {
      setLoading(false);
    }
  };

  const getDiscrepancyType = (discrepancy) => {
    const types = [];
    if (discrepancy.diferenciaPuntos > 0) types.push('Puntos');
    if (discrepancy.diferenciaAcumulado > 0.01) types.push('Acumulado');
    if (discrepancy.limiteSuperado) types.push('Límite Superado');
    return types.join(', ');
  };

  const getDiscrepancySeverity = (discrepancy) => {
    if (discrepancy.limiteSuperado) return 'high';
    if (discrepancy.diferenciaPuntos > 5 || discrepancy.diferenciaAcumulado > 50) return 'high';
    if (discrepancy.diferenciaPuntos > 2 || discrepancy.diferenciaAcumulado > 10) return 'medium';
    return 'low';
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Análisis de Discrepancias de Puntos</h3>
            <p className="text-sm text-gray-600 mt-1">
              {clienteId
                ? 'Analizando tickets de un cliente específico'
                : 'Analizando todos los tickets de la plataforma'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Action Button */}
          {!report && (
            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">¿Qué hace este análisis?</p>
                    <p>
                      Compara los puntos y montos acumulados almacenados en los documentos de tickets
                      con los valores calculados desde el historial de consumiciones y canjes. Detecta
                      discrepancias que pueden indicar problemas en la lógica de acumulación o
                      actualizaciones manuales incorrectas.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleAnalyze}
                disabled={loading}
                className={`
                  w-full flex items-center justify-center gap-2 px-6 py-3 rounded-md font-medium transition-colors
                  ${
                    loading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }
                `}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    Analizando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSearch} />
                    {clienteId ? 'Analizar Cliente' : 'Analizar Todos los Tickets'}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-gray-400" />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <div className="flex items-start gap-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5" />
                <div>
                  <h5 className="font-semibold text-red-900 mb-1">Error</h5>
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Report */}
          {report && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Clientes Analizados</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">
                        {report.totalClientes || 0}
                      </p>
                    </div>
                    <div className="bg-blue-100 rounded-full p-3">
                      <FontAwesomeIcon icon={faChartBar} className="text-blue-600 text-xl" />
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Tickets Analizados</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">
                        {report.totalTickets || 0}
                      </p>
                    </div>
                    <div className="bg-purple-100 rounded-full p-3">
                      <FontAwesomeIcon icon={faChartBar} className="text-purple-600 text-xl" />
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Con Discrepancias</p>
                      <p className="text-3xl font-bold text-red-600 mt-2">
                        {report.ticketsConDiscrepancias || 0}
                      </p>
                      {report.totalTickets > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {((report.ticketsConDiscrepancias / report.totalTickets) * 100).toFixed(1)}%
                        </p>
                      )}
                    </div>
                    <div className="bg-red-100 rounded-full p-3">
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="text-red-600 text-xl"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Límite Superado</p>
                      <p className="text-3xl font-bold text-orange-600 mt-2">
                        {report.resumen?.ticketsConLimiteSuperado || 0}
                      </p>
                    </div>
                    <div className="bg-orange-100 rounded-full p-3">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-600 text-xl" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Details */}
              {report.resumen && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Resumen de Discrepancias</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Discrepancias de Puntos</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {report.resumen.discrepanciasPuntos || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Discrepancias de Acumulado</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {report.resumen.discrepanciasAcumulado || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Tickets con Límite Superado</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {report.resumen.ticketsConLimiteSuperado || 0}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Discrepancies Table */}
              {report.discrepancias && report.discrepancias.length > 0 ? (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-gray-200">
                    <h4 className="font-semibold text-gray-900">
                      Discrepancias Encontradas ({report.discrepancias.length})
                    </h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Cliente
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Ticket
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Tipo
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Puntos
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Acumulado
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Problema
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {report.discrepancias.map((discrepancy, index) => {
                          const severity = getDiscrepancySeverity(discrepancy);
                          return (
                            <tr
                              key={index}
                              className={`hover:bg-gray-50 ${
                                severity === 'high' ? 'bg-red-50' : ''
                              }`}
                            >
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {discrepancy.clienteNombre}
                                </div>
                                <div className="text-xs text-gray-500">{discrepancy.clienteEmail}</div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{discrepancy.ticketCodigo}</div>
                                <div className="text-xs text-gray-500">{discrepancy.marcaNombre}</div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {discrepancy.tipoFidelizacion}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm">
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-600">
                                      Doc: <strong>{discrepancy.puntosDocumento}</strong>
                                    </span>
                                    <span className="text-gray-400">→</span>
                                    <span className="text-gray-900">
                                      Calc: <strong>{discrepancy.puntosCalculados}</strong>
                                    </span>
                                  </div>
                                  {discrepancy.diferenciaPuntos > 0 && (
                                    <div className="text-xs text-red-600 mt-1">
                                      Diferencia: {discrepancy.diferenciaPuntos}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm">
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-600">
                                      Doc: <strong>{discrepancy.acumuladoDocumento?.toFixed(2)}</strong>
                                    </span>
                                    <span className="text-gray-400">→</span>
                                    <span className="text-gray-900">
                                      Calc: <strong>{discrepancy.acumuladoCalculado?.toFixed(2)}</strong>
                                    </span>
                                  </div>
                                  {discrepancy.diferenciaAcumulado > 0.01 && (
                                    <div className="text-xs text-red-600 mt-1">
                                      Diferencia: {discrepancy.diferenciaAcumulado?.toFixed(2)}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="space-y-1">
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSeverityColor(
                                      severity
                                    )}`}
                                  >
                                    {getDiscrepancyType(discrepancy)}
                                  </span>
                                  {discrepancy.limiteSuperado && (
                                    <div className="text-xs text-orange-600">
                                      Límite: {discrepancy.limitePuntosTicket}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm">
                                <button
                                  onClick={() => setSelectedDiscrepancy(discrepancy)}
                                  className="text-blue-600 hover:text-blue-900 mr-3"
                                  title="Ver detalles"
                                >
                                  <FontAwesomeIcon icon={faInfoCircle} />
                                </button>
                                {discrepancy.url && (
                                  <a
                                    href={`/tickets/${discrepancy.url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-600 hover:text-green-900"
                                    title="Ver ticket"
                                  >
                                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                                  </a>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : report && report.discrepancias?.length === 0 ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-green-500 mb-4" />
                  <h4 className="text-lg font-semibold text-green-900 mb-2">
                    ¡No se encontraron discrepancias!
                  </h4>
                  <p className="text-sm text-green-800">
                    Todos los tickets tienen valores consistentes con sus historiales.
                  </p>
                </div>
              ) : null}

              {/* Detail Modal */}
              {selectedDiscrepancy && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-900">Detalles de Discrepancia</h4>
                      <button
                        onClick={() => setSelectedDiscrepancy(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                    <div className="p-6 space-y-4">
                      <div>
                        <p className="text-sm text-gray-600">Cliente</p>
                        <p className="text-base font-medium text-gray-900">
                          {selectedDiscrepancy.clienteNombre} ({selectedDiscrepancy.clienteEmail})
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Ticket</p>
                        <p className="text-base font-medium text-gray-900">
                          {selectedDiscrepancy.ticketCodigo}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Marca</p>
                        <p className="text-base font-medium text-gray-900">
                          {selectedDiscrepancy.marcaNombre}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-2">Puntos</p>
                          <div className="bg-gray-50 rounded p-3">
                            <p className="text-sm">
                              Documento: <strong>{selectedDiscrepancy.puntosDocumento}</strong>
                            </p>
                            <p className="text-sm">
                              Calculado: <strong>{selectedDiscrepancy.puntosCalculados}</strong>
                            </p>
                            <p className="text-sm text-red-600">
                              Diferencia: <strong>{selectedDiscrepancy.diferenciaPuntos}</strong>
                            </p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-2">Acumulado</p>
                          <div className="bg-gray-50 rounded p-3">
                            <p className="text-sm">
                              Documento: <strong>{selectedDiscrepancy.acumuladoDocumento?.toFixed(2)}</strong>
                            </p>
                            <p className="text-sm">
                              Calculado: <strong>{selectedDiscrepancy.acumuladoCalculado?.toFixed(2)}</strong>
                            </p>
                            <p className="text-sm text-red-600">
                              Diferencia: <strong>{selectedDiscrepancy.diferenciaAcumulado?.toFixed(2)}</strong>
                            </p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Historial</p>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-sm">
                            Consumiciones: <strong>{selectedDiscrepancy.historialConsumiciones}</strong>
                          </p>
                          <p className="text-sm">
                            Canjes: <strong>{selectedDiscrepancy.historialCanjeos}</strong>
                          </p>
                        </div>
                      </div>
                      {selectedDiscrepancy.limiteSuperado && (
                        <div className="bg-orange-50 border border-orange-200 rounded p-3">
                          <p className="text-sm text-orange-800">
                            <strong>⚠️ Límite Superado:</strong> Los puntos calculados ({' '}
                            {selectedDiscrepancy.puntosCalculados}) superan el límite permitido (
                            {selectedDiscrepancy.limitePuntosTicket}) para esta marca.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleAnalyze}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Re-analizar
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketsDiscrepanciesAnalysis;

